import type { UserGaugeData, UserTokenBalance } from './types';

// Fetch user's gauge data (staked balance and claimable rewards)
export async function fetchUserGaugeData(gaugeAddress: string, walletAddress: string): Promise<UserGaugeData> {
  const response = await fetch(`/api/gauge/${gaugeAddress}/user/${walletAddress}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch user gauge data: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

// Fetch user's token balance (for LP tokens)
export async function fetchUserTokenBalance(tokenAddress: string, walletAddress: string): Promise<UserTokenBalance> {
  const response = await fetch(`/api/token/${tokenAddress}/balance/${walletAddress}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch token balance: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

// Fetch native ETH balance
export async function fetchNativeEthBalance(walletAddress: string): Promise<{ balance: string; address: string }> {
  const response = await fetch(`/api/wallet/${walletAddress}/eth-balance`);

  if (!response.ok) {
    throw new Error(`Failed to fetch native ETH balance: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

// Fetch pool metrics for a specific pool (from pool-aggregator cache)
export async function fetchPoolMetrics(poolAddress: string, options?: { bypassCache?: boolean }) {
  const bypassCache = options?.bypassCache || false;
  const url = `/api/pools/${poolAddress}${bypassCache ? '?bypassCache=true' : ''}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch pool metrics: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

// Fetch all pool metrics at once (more efficient for multiple pools)
export async function fetchAllPoolMetrics(options?: { bypassCache?: boolean }) {
  const bypassCache = options?.bypassCache || false;
  const url = `/api/pools/all${bypassCache ? '?bypassCache=true' : ''}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch all pool metrics: ${response.status} ${response.statusText}`);
  }

  return response.json() as Promise<Record<string, { tvlUsd: number; stakedUsd: number; aprCurrent: number; aprCurrentPct: number }>>;
}

// Fetch user's slipstream NFT positions
export async function fetchUserSlipstreamPositions(gaugeAddress: string, walletAddress: string) {
  const response = await fetch(`/api/gauge/${gaugeAddress}/nfts/${walletAddress}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch slipstream NFT positions: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

// Fetch token decimals
export async function fetchTokenDecimals(tokenAddress: string) {
  const response = await fetch(`/api/token/${tokenAddress}/decimals`);

  if (!response.ok) {
    throw new Error(`Failed to fetch token decimals: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

// Fetch token total supply
export async function fetchTokenTotalSupply(tokenAddress: string, options?: { bypassCache?: boolean }) {
  const bypassCache = options?.bypassCache || false;
  const url = `/api/pools/${tokenAddress}/totalSupply${bypassCache ? '?bypassCache=true' : ''}`;

  if (import.meta.env.DEV) {
    console.log(`🔍 fetchTokenTotalSupply called with:`, { tokenAddress, url, bypassCache });
  }

  try {
    const response = await fetch(url);

    if (!response.ok) {
      // Try to get error details from response
      let errorDetails = '';
      try {
        const errorBody = await response.json();
        errorDetails = JSON.stringify(errorBody);
      } catch {
        errorDetails = await response.text();
      }

      const errorMessage = `Failed to fetch token total supply: ${response.status} ${response.statusText}${errorDetails ? ` - ${errorDetails}` : ''}`;

      if (import.meta.env.DEV) {
        console.error(`❌ fetchTokenTotalSupply failed:`, {
          tokenAddress,
          status: response.status,
          statusText: response.statusText,
          errorDetails
        });
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();

    if (import.meta.env.DEV) {
      console.log(`✅ fetchTokenTotalSupply succeeded for ${tokenAddress}:`, data);
    }

    return data;
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error(`❌ fetchTokenTotalSupply exception for ${tokenAddress}:`, err);
    }
    throw err;
  }
}

// Fetch NFT position details (liquidity, ticks, tokens, etc.)
export async function fetchNftPositionDetails(tokenId: string) {
  const response = await fetch(`/api/nft/position/${tokenId}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch NFT position details: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

// Fetch NPM address from gauge contract
export async function fetchGaugeNpmAddress(gaugeAddress: string) {
  const response = await fetch(`/api/gauge/${gaugeAddress}/npm`);

  if (!response.ok) {
    throw new Error(`Failed to fetch gauge NPM address: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

// Fetch pool sugar data (includes current tick)
export async function fetchPoolSugarData(poolAddress: string, options?: { bypassCache?: boolean }) {
  const bypassCache = options?.bypassCache || false;
  const url = `/api/pools/${poolAddress}/sugar${bypassCache ? '?bypassCache=true' : ''}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch pool sugar data: ${response.status} ${response.statusText}`);
  }

  return response.json() as Promise<{
    kind: "slipstream_sugar";
    tick?: number;
    emissions?: string;
    gauge_liquidity?: string;
    // ... other fields
  }>;
}

// Fetch token price from price-aggregator (cached) or market-data (direct)
export async function fetchTokenPrice(tokenAddress: string, options?: { bypassCache?: boolean }) {
  const bypassCache = options?.bypassCache || false;

  // Try price-aggregator first (cached)
  if (!bypassCache) {
    try {
      const response = await fetch(`/api/prices?addresses=${tokenAddress}`);
      if (response.ok) {
        const data = await response.json() as Record<string, { priceUsd: number; asOf: string; source: string } | { error: string }>;
        const priceData = data[tokenAddress.toLowerCase()];
        if (priceData) {
          if ('error' in priceData) {
            console.warn(`Price aggregator returned error for ${tokenAddress}:`, priceData.error);
            // Fall through to market-data
          } else {
            // Normalize to match market-data response format
            return {
              token: { symbol: '' }, // Will be filled by consumer if needed
              priceUsd: priceData.priceUsd,
              asOf: priceData.asOf,
              source: priceData.source,
            };
          }
        }
      }
    } catch (err) {
      console.warn('Price aggregator failed, falling back to market-data', err);
      // Fall through to market-data
    }
  }

  // Fallback to market-data (direct call, always fresh)
  const response = await fetch(`/api/price?by=address&address=${tokenAddress}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch token price: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

// Fetch all token prices from price-aggregator
export async function fetchAllPrices() {
  const response = await fetch(`/api/prices/all`);

  if (!response.ok) {
    throw new Error(`Failed to fetch all prices: ${response.status} ${response.statusText}`);
  }

  return response.json() as Promise<Record<string, { priceUsd: number; asOf: string; source: string }>>;
}

// Helper to format wei to readable tokens (AERO has 18 decimals)
export function formatFromWei(weiAmount: string, decimals: number = 18): number {
  const wei = BigInt(weiAmount);
  const divisor = BigInt(10 ** decimals);
  return Number(wei) / Number(divisor);
}

// Helper to calculate user's position value in USD
export function calculatePositionValueUsd(
  lpBalance: number,
  poolTvlUsd: number,
  totalLpSupply: number
): number {
  if (totalLpSupply === 0) return 0;

  // Calculate user's share of the pool
  const userShare = lpBalance / totalLpSupply;

  // Calculate USD value based on their share of total TVL
  return userShare * poolTvlUsd;
}

